// Centralised error-code → plain-English copy. Multiple surfaces (save,
// share, calculate, PDF, list, share-link-view) had their own local
// describeError() functions that drifted — some leaked raw codes into
// the demo path. This module owns the canonical mapping; callers pass
// the action verb so the same code reads naturally in different chrome.
//
// describeError('save', 'name_taken')
//   -> "You already have a valuation with this name."
// describeError('share', 'unauthorized')
//   -> "Couldn't generate share link — please sign in again."

const ACTION_LABEL = {
  save: 'save',
  load: 'load',
  share: 'generate share link',
  pdf: 'generate PDF',
  calc: 'calculate',
  list: 'load valuations',
  match: 'load reference benchmarks',
  presets: 'load presets',
  disease: 'load disease catalog',
  shared: 'open shared valuation',
};

// Universal codes — apply regardless of action.
function describeCommon(code) {
  switch (code) {
    case 'unauthorized':
      return 'Please sign in again.';
    case 'profile_completion_required':
      return 'Your profile needs to be completed first.';
    case 'phi_attestation_required':
      return 'Complete the data-entry attestation under Account preferences (profile menu) before using the calculator.';
    case 'db_not_configured':
    case 'valuations_unavailable':
      return "The local database isn't running. Bring up postgres with `docker compose up -d`.";
    case 'name_required':
      return 'Please enter a name.';
    case 'name_taken':
      return 'You already have a valuation with this name. Pick a different one.';
    case 'not_found':
      return "That valuation isn't available — it may have been deleted.";
    case 'network_error':
    case 'fetch_failed':
      return "Can't reach the server. Check your connection and try again.";
    case 'pdf_render_unavailable':
    case 'pdf_render_error':
      return 'The PDF renderer is offline. Try again in a moment.';
    case 'share_expired':
    case 'token_expired':
      return 'This share link has expired (7-day limit).';
    case 'invalid_token':
    case 'bad_signature':
      return 'This share link is malformed.';
    case 'missing_token':
      return 'No share token in the URL.';
    case 'invalid_json':
      return 'The server rejected the request payload.';
    case 'rate_limited':
      return 'Too many requests. Wait a moment and try again.';
    case 'load_failed':
    case 'create_failed':
    case 'update_failed':
    case 'delete_failed':
    case 'get_failed':
      return null; // fall through to the generic "Couldn't <action> (<code>)"
    default:
      return null;
  }
}

// describeError(action, code) returns a single user-facing sentence.
// action determines the "Couldn't <verb>" prefix; code drives the body.
export function describeError(action, code) {
  if (!code) return null;
  const verb = ACTION_LABEL[action] || 'continue';
  const common = describeCommon(code);
  if (common) return `Couldn't ${verb} — ${common.charAt(0).toLowerCase()}${common.slice(1)}`;
  return `Couldn't ${verb} (${code}). Try again.`;
}

// describeSharedValuationError differentiates the four states the public
// share-link viewer should communicate distinctly: expired, malformed,
// missing token, anything else (server-down / not_found / generic).
export function describeSharedValuationError(code) {
  switch (code) {
    case 'share_expired':
    case 'token_expired':
      return {
        title: 'This share link has expired.',
        body: 'Share links are valid for 7 days. Ask whoever shared it with you to generate a fresh one.',
      };
    case 'invalid_token':
    case 'bad_signature':
      return {
        title: 'This share link is malformed.',
        body: "The URL doesn't carry a valid signature. Check that you copied the full link — it should end with a long token.",
      };
    case 'missing_token':
      return {
        title: 'No share token in this URL.',
        body: 'The link should look like /share/v1.<token>. Ask whoever shared it for the full URL.',
      };
    case 'not_found':
      return {
        title: 'This valuation is no longer available.',
        body: 'The owner may have deleted it, or the share link may have been revoked. Ask them to generate a new one.',
      };
    case 'network_error':
    case 'fetch_failed':
      return {
        title: "Can't reach the server.",
        body: 'Check your connection and reload the page.',
      };
    default:
      return {
        title: 'This share link is no longer valid.',
        body: 'It may have expired, been revoked, or the valuation may have been deleted. Ask whoever shared it with you to generate a new one.',
      };
  }
}
